<template>
  <div class="app-container">
    <div>
      <el-select
        v-model="selectedWorkshop"
        :clearable="true"
        class="search-select"
        type="select"
        style="margin-bottom: 10px"
        :placeholder="$t('common.workshop')"
        @input="handleWorkshopChange($event)"
      >
        <el-option v-for="workshop in workshops" :key="workshop.id" :label="workshop.name" :value="workshop.id" />
      </el-select>

      <el-select
        v-model="selectedClient"
        :clearable="true"
        class="search-select"
        type="select"
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('invoice.clientName')"
        @input="handleClientChange($event)"
      >
        <el-option v-for="client in clients" :key="client.id" :label="client.name" :value="client.id" />
      </el-select>

      <el-input
        v-model="month"
        class="search-select"
        type="number"
        :min="1"
        :max="12"
        :placeholder="$t('invoice.month')"
        style="width: 20%; padding-bottom: 10px"
        @input="refreshList($event)"
      />

      <el-input
        v-model="year"
        class="search-select"
        type="number"
        :min="2020"
        :max="2099"
        :placeholder="$t('invoice.year')"
        style="width: 20%; padding-bottom: 10px"
        @input="refreshList($event)"
      />

      <el-input
        v-model="invoiceNumber"
        class="search-select"
        type="select"
        :placeholder="$t('invoice.invoiceNumber')"
        style="width: 20%; padding-bottom: 10px"
        @input="refreshList($event)"
      />

      <div style="float:right;">
        <editor-button
          :disabled="!itemsAreSelected"
          :icon-only="false"
          :loading="creatingDocuments"
          button-translation="invoice.print"
          action="primary"
          @click="createDocuments()"
        />
      </div>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
    </div>
    <div ref="tableContainer">
      <pagination
        :page-sizes="[50, 100]"
        :total="loadedData.total"
        :page.sync="page"
        :limit.sync="pageSize"
        @pagination="refreshList"
      >
      </pagination>
      <el-table
        ref="invoicesList"
        :data="items"
        border
        row-key="internalInvoiceID"
        fit
        :max-height="getMaxTableHeight()"
        @selection-change="handleSelectionChange"
      >
        <el-table-column :label="$t('invoice.invoiceNumber')" prop="invoiceNumber" width="120px">
          <template slot-scope="scope">
            <span>{{ scope.row['invoiceNumber'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.invoiceDate')" prop="invoiceDate" width="120px">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['invoiceDate']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.clientName')" prop="clientName">
          <template slot-scope="scope">
            <span>{{ scope.row['clientName'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.salesInvoiceExportedOn')" prop="salesInvoiceExportedOn" width="190">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['salesInvoiceExportedOn']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.purchaseInvoiceExportedOn')" prop="purchaseInvoiceExportedOn" width="190">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['purchaseInvoiceExportedOn']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.numberOfOrders')" width="120px">
          <template slot-scope="scope">
            <span>{{ scope.row['numberOfOrders'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.totalAmountExcludingVAT')" width="150px" align="right">
          <template slot-scope="scope">
            <span>{{ formatPrice(scope.row['totalAmount']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.totalAmountIncludingVAT')" width="150px" align="right">
          <template slot-scope="scope">
            <span>{{ formatPrice(scope.row['totalAmountIncludingVAT']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.currency')" width="100px" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row['currencyISOCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          fixed="right"
          type="selection"
          :reserve-selection="true"
          width="40"
          align="center"
          :selectable="() => true"
        >
        </el-table-column>
      </el-table>
      <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        :close-on-click-modal="false"
        :append-to-body="true"
        :width="'90%'"
        @closed="handleDialogClosed"
      >
        <document-viewer-dialog :pdfs="createdDocuments"> </document-viewer-dialog>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import Pagination from '@/components/Pagination';
import { toDateTimeString } from '@/utils/date';
import DocumentViewerDialog from '@/pages/invoicing/DocumentViewerDialog';

export default {
  name: 'Planning',
  components: {
    EditorButton,
    Pagination,
    DocumentViewerDialog
  },
  data() {
    return {
      loading: false,
      loadedData: { items: [], total: 0 },
      errorMsg: '',
      workshopID: null,
      clientID: null,
      month: null,
      year: null,
      invoiceNumber: null,
      page: 1,
      pageSize: 50,
      multipleSelection: [],
      creatingDocuments: false,
      dialogTitle: '',
      dialogVisible: false,
      createdDocuments: []
    };
  },
  computed: {
    listParams() {
      return {
        workshopID: this.workshopID || 0,
        clientID: this.clientID,
        month: this.month,
        year: this.year,
        invoiceNumber: this.invoiceNumber,
        page: this.page,
        pageSize: this.pageSize
      };
    },
    items() {
      return this.loadedData.items;
    },
    globalSelectedDomain() {
      return this.$store.getters['domain/getDefaultSelectValueDomainID'];
    },
    selectedWorkshop: {
      get() {
        return this.workshopID;
      },
      set(newWorkshop) {
        return (this.workshopID = newWorkshop);
      }
    },
    selectedClient: {
      get() {
        return this.clientID;
      },
      set(newClient) {
        return (this.clientID = newClient);
      }
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    workshops() {
      return this.$store.getters.workshops;
    },
    clients() {
      const domainID = this.globalSelectedDomain;
      if (domainID > 0) {
        return this.$store.getters['client/loadedItems'].filter(e => e.domainID === domainID);
      } else {
        return this.$store.getters['client/loadedItems'];
      }
    },
    itemsAreSelected() {
      return +this.multipleSelection.length;
    }
  },
  watch: {
    globalSelectedDomain(newVal) {
      this.refreshList();
      this.clearSelection();
    }
  },
  created() {
    this.$store.dispatch('client/getItems');
  },
  methods: {
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    handleDialogClosed() {
      this.createdDocuments = [];
    },
    createDocuments() {
      this.creatingDocuments = true;
      this.$store
        .dispatch('internalInvoice/createDocuments', {
          invoices: this.multipleSelection.map(i => i.internalInvoiceID)
        })
        .then(data => {
          this.createdDocuments = data;
          this.dialogVisible = true;
        })
        .finally(() => {
          this.creatingDocuments = false;
          this.refreshList();
          this.clearSelection();
        });
    },
    refreshList() {
      this.loading = true;
      this.$store
        .dispatch('internalInvoice/getInvoices', this.listParams)
        .then(data => {
          this.loadedData.items = data.items;
          this.loadedData.total = data.total;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleWorkshopChange() {
      this.refreshList();
      this.clearSelection();
    },
    handleClientChange() {
      this.refreshList();
      this.clearSelection();
    },
    formatPrice(cellValue) {
      if (isNaN(cellValue)) {
        return '';
      }
      return `${cellValue.toFixed(2)}`;
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - 40;
      }
    },
    clearSelection() {
      this.multipleSelection = [];
      this.$refs.invoicesList.clearSelection();
    },
    formatDate(cellValue) {
      return cellValue && toDateTimeString(cellValue, 'DD-MM-YYYY');
    }
  }
};
</script>
